import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { showError, showSuccess } from '@/lib/swal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { leaveRequestService } from '@/services/leaveRequestService'
import { userService } from '@/services/userService'
import type { LeaveRequestSavePayload, LeaveRequestLookupItem, LeaveRequestEmployeeItem, EmployeeContractLeaveBalance } from '@/api/types'

export const LeaveRequestDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0
  const userId = user?.userId ?? 0

  const isNew = params.id === 'new' || !params.id
  const editId = !isNew ? Number(params.id) : 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [empId, setEmpId] = useState<number>(0)
  const [leaveBalance, setLeaveBalance] = useState<EmployeeContractLeaveBalance | null>(null)

  const [leaveTypes, setLeaveTypes] = useState<LeaveRequestLookupItem[]>([])
  const [employees, setEmployees] = useState<LeaveRequestEmployeeItem[]>([])

  const [leaveTypeId, setLeaveTypeId] = useState<number>(0)
  const [substituteEmpId, setSubstituteEmpId] = useState<number>(0)
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [details, setDetails] = useState('')
  const [status, setStatus] = useState<number>(0)
  const [approvalDate, setApprovalDate] = useState<string | null>(null)
  const [isEditable, setIsEditable] = useState<number>(0)

  const req = t('leaveRequestPage.requiredMark')

  const calcRequestedLeaveDays = (from: string, to: string) => {
    try {
      if (!from || !to) return 0
      const fromD = new Date(from + 'T00:00:00')
      const toD = new Date(to + 'T00:00:00')
      if (Number.isNaN(fromD.getTime()) || Number.isNaN(toD.getTime())) return 0
      if (toD.getTime() < fromD.getTime()) return 0
      const msPerDay = 24 * 60 * 60 * 1000
      return Math.floor((toD.getTime() - fromD.getTime()) / msPerDay) + 1
    } catch {
      return 0
    }
  }

  useEffect(() => {
    if (!branchId || !userId) return
    const load = async () => {
      try {
        const u = await userService.getUserById(branchId, userId)
        const eId = u?.empId ?? u?.employeeId ?? 0
        setEmpId(eId)
        if (eId) {
          const contract = await leaveRequestService.getEmployeeContract(eId, branchId)
          setLeaveBalance(contract ?? null)
        }
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? t('leaveRequestPage.loadEmployeeInfoFailed'))
      }
    }
    load()
  }, [branchId, userId])

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [types, emps] = await Promise.all([
          leaveRequestService.getLeaveTypes(),
          leaveRequestService.getActiveEmployees(branchId),
        ])
        setLeaveTypes(types || [])
        setEmployees(emps || [])
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? t('leaveRequestPage.loadFormFailed'))
      }
    }
    loadLookups()
  }, [branchId])

  useEffect(() => {
    if (!branchId || isNew || !editId) return
    const load = async () => {
      try {
        setLoading(true)
        const vm = await leaveRequestService.getById(editId, branchId)
        if (!vm) {
          navigate('/self-services/leave-requests')
          return
        }
        setLeaveTypeId(vm.leaveTypeId ?? 0)
        setSubstituteEmpId(vm.substituteEmpId ?? 0)
        setFromDate(vm.fromDate ? String(vm.fromDate).slice(0, 10) : '')
        setToDate(vm.toDate ? String(vm.toDate).slice(0, 10) : '')
        setDetails(vm.details ?? '')
        setStatus(vm.status ?? 0)
        setApprovalDate(vm.approvalDate ?? null)
        setIsEditable(vm.isEditable ?? 0)
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? t('leaveRequestPage.loadFailed'))
        navigate('/self-services/leave-requests')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, isNew, editId, navigate])

  const leaveTypeOptions = useMemo((): SearchableSelectOption[] => {
    return [
      { value: '0', label: t('leaveRequestPage.placeholderSelectLeaveType') },
      ...leaveTypes.filter((x) => x.id).map((x) => ({
        value: String(x.id),
        label: x.name ?? x.nameEn ?? x.lookupNameEn ?? t('leaveRequestPage.typeFallback', { id: x.id }),
      })),
    ]
  }, [leaveTypes, t])

  const substituteOptions = useMemo((): SearchableSelectOption[] => {
    return [
      { value: '0', label: t('leaveRequestPage.placeholderSelectEmployee') },
      ...employees.filter((e) => e.id).map((e) => ({
        value: String(e.empId ?? e.id),
        label: e.fullName ?? t('leaveRequestPage.employeeFallback', { id: e.id }),
      })),
    ]
  }, [employees, t])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) return
    if (!empId) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastEmployeeLink'))
      return
    }
    if (!leaveTypeId) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastLeaveType'))
      return
    }
    if (!substituteEmpId) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastSubstitute'))
      return
    }
    if (!fromDate) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastFromDate'))
      return
    }
    if (!toDate) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastToDate'))
      return
    }
    if (!details.trim()) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastDetails'))
      return
    }

    if (!leaveBalance) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastLeaveBalanceUnavailable'))
      return
    }

    const requestedDays = calcRequestedLeaveDays(fromDate, toDate)
    const remainingLeave = leaveBalance?.remainingLeave ?? (leaveBalance as any)?.leavesBalance ?? 0
    if (requestedDays <= 0) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastInvalidDateRange'))
      return
    }
    if (remainingLeave <= 0) {
      await showError(t('leaveRequestPage.saveFailedTitle'), t('leaveRequestPage.toastZeroRemaining'))
      return
    }
    if (requestedDays > remainingLeave) {
      await showError(
        t('leaveRequestPage.saveFailedTitle'),
        t('leaveRequestPage.toastRequestedExceedsRemaining', { requested: requestedDays, remaining: remainingLeave })
      )
      return
    }

    const dateFromIso = fromDate ? new Date(fromDate + 'T00:00:00.000Z').toISOString() : ''
    const dateToIso = toDate ? new Date(toDate + 'T00:00:00.000Z').toISOString() : ''

    const payload: LeaveRequestSavePayload = {
      id: isNew ? 0 : editId,
      empId,
      leaveTypeId,
      dateFrom: dateFromIso,
      dateTo: dateToIso,
      substituteId: substituteEmpId,
      reason: details.trim(),
      status,
      approvalDate,
      branchId,
      isEditable,
    }

    try {
      setSaving(true)
      if (isNew) {
        await leaveRequestService.create(payload)
        await showSuccess(t('leaveRequestPage.savedTitle'), t('leaveRequestPage.savedMessage'), 3000)
      } else {
        await leaveRequestService.update(payload)
        await showSuccess(t('leaveRequestPage.updatedTitle'), t('leaveRequestPage.updatedMessage'), 3000)
      }
      navigate('/self-services/leave-requests')
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? t('leaveRequestPage.saveFailed')
      await showError(isNew ? t('leaveRequestPage.saveFailedTitle') : t('leaveRequestPage.updateFailedTitle'), msg, 5000)
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('leaveRequestPage.pageTitle')} description={t('leaveRequestPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">{t('leaveRequestPage.selectBranchFirst')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('leaveRequestPage.pageTitle')} description={t('leaveRequestPage.pageDescription')} />

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-md border border-input bg-muted/30 p-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('leaveRequestPage.statLeaveBalance')}</p>
                <p className="text-lg font-semibold">{leaveBalance?.leaveBalance ?? 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('leaveRequestPage.statRequestedLeave')}</p>
                <p className="text-lg font-semibold">{leaveBalance?.requestedLeave ?? 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('leaveRequestPage.statRemainingLeave')}</p>
                <p className="text-lg font-semibold">{leaveBalance?.remainingLeave ?? 0}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <SearchableSelect
                  label={`${req} ${t('leaveRequestPage.labelLeaveType')}`}
                  options={leaveTypeOptions}
                  value={leaveTypeId ? String(leaveTypeId) : '0'}
                  onValueChange={(v) => setLeaveTypeId(Number(v) || 0)}
                  placeholder={t('leaveRequestPage.placeholderSelectLeaveType')}
                  searchPlaceholder={t('leaveRequestPage.searchShort')}
                  emptyMessage={t('leaveRequestPage.emptyLeaveType')}
                />
              </div>
              <div className="space-y-2">
                <SearchableSelect
                  label={`${req} ${t('leaveRequestPage.labelSubstitute')}`}
                  options={substituteOptions}
                  value={substituteEmpId ? String(substituteEmpId) : '0'}
                  onValueChange={(v) => setSubstituteEmpId(Number(v) || 0)}
                  placeholder={t('leaveRequestPage.placeholderSelectEmployee')}
                  searchPlaceholder={t('leaveRequestPage.searchEmployee')}
                  emptyMessage={t('leaveRequestPage.emptyEmployee')}
                />
              </div>
              <div className="space-y-2">
                <Label>{req} {t('leaveRequestPage.labelFrom')}</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{req} {t('leaveRequestPage.labelTo')}</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>{req} {t('leaveRequestPage.labelDetails')}</Label>
                <Textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder={t('leaveRequestPage.placeholderDetails')} rows={4} />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" disabled={saving || loading} onClick={() => navigate('/self-services/leave-requests')}>
                {t('leaveRequestPage.return')}
              </Button>
              <Button type="submit" disabled={saving || loading}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('leaveRequestPage.saving') : isNew ? t('leaveRequestPage.save') : t('leaveRequestPage.update')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
